import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Home, Baby, Edit2, Trash2, X, Check } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import DailyBriefingBar from '../components/DailyBriefingBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryConfig = {
  family: { 
    name: 'Family', 
    icon: Home, 
    color: '#C2410C', 
    description: 'Family matters and household management',
    subcategories: [
      { id: 'subscriptions', name: 'Subscriptions', icon: '📱' },
      { id: 'bills', name: 'Bills', icon: '💳' },
      { id: 'appointments', name: 'Appointments', icon: '📅' },
      { id: 'medical', name: 'Medical & Health', icon: '🏥' },
      { id: 'holidays', name: 'Holidays & Travel', icon: '✈️' },
      { id: 'todos', name: 'To-Dos', icon: '✓' },
      { id: 'admin', name: 'Admin & Upkeep', icon: '📄' }
    ]
  },
  kids: { 
    name: 'Kids', 
    icon: Baby, 
    color: '#EC4899', 
    description: 'Children activities and schedules',
    subcategories: [
      { id: 'activities', name: 'Activities & Camps', icon: '🎨' },
      { id: 'school', name: 'Term Dates & INSET', icon: '🏫' },
      { id: 'holidays', name: 'Public Holidays', icon: '🏖️' },
      { id: 'clubs', name: 'Clubs', icon: '⚽' },
      { id: 'homework', name: 'Homework & School Work', icon: '📚' },
      { id: 'appointments', name: 'Appointments', icon: '👶' },
      { id: 'kiera', name: 'Kiera — Documents', icon: '📋' }
    ]
  }
};

const PersonalCategory = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [form, setForm] = useState({ title: '', date: '', notes: '', subcategory: '' });
  const [editingItem, setEditingItem] = useState(null);

  const config = categoryConfig[category];
  const Icon = config?.icon;

  useEffect(() => {
    if (config) {
      fetchItems();
      if (config.subcategories.length > 0) {
        setSelectedSubcategory(config.subcategories[0].id);
      }
    }
  }, [category]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/personal?category=${category}`,
        { withCredentials: true }
      );
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
      // Mock data for preview
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(
          `${API_URL}/api/personal/${editingItem.id}`,
          { title: form.title, date: form.date, notes: form.notes, subcategory: form.subcategory },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/personal`,
          { category, ...form },
          { withCredentials: true }
        );
      }
      setForm({ title: '', date: '', notes: '', subcategory: '' });
      setShowForm(false);
      setEditingItem(null);
      fetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({ title: item.title, date: item.date || '', notes: item.notes || '', subcategory: item.subcategory || '' });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/personal/${itemId}`, { withCredentials: true });
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleMarkDone = async (item) => {
    try {
      await axios.put(`${API_URL}/api/personal/${item.id}`, { status: 'done' }, { withCredentials: true });
      fetchItems();
    } catch (error) {
      console.error('Error marking done:', error);
    }
  };

  const getItemsBySubcategory = (subcategoryId) => {
    return items.filter(item => item.subcategory === subcategoryId);
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <p className="text-neutral-600">Category not found</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: config.color }}></div>
          <p className="mt-4 text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid={`personal-${category}`}>
      <DailyBriefingBar />
      {/* Header */}
      <header className="bg-white shadow-sm" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 mb-4 transition-all duration-200"
            style={{ color: '#8B7355' }}
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: config.color + '20' }}
            >
              <Icon className="w-8 h-8" style={{ color: config.color }} />
            </div>
            <div>
              <h1 className="text-4xl font-light tracking-tight" style={{ color: config.color }}>{config.name}</h1>
              <p className="mt-1" style={{ color: '#8B7355' }}>{config.description}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Add Item Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-6 py-3 rounded-sm transition-all duration-200 hover:shadow-md"
            style={{ backgroundColor: config.color, color: 'white' }}
            data-testid="add-item-button"
          >
            <Plus className="w-5 h-5" />
            <span>Add Item</span>
          </button>
        </div>

        {/* Add Item Form */}
        {showForm && (
          <div className="bg-white border rounded-sm shadow-sm p-8 mb-6" style={{ borderColor: '#E5E1DB' }} data-testid="add-item-form">
            <h2 className="text-2xl font-light mb-6" style={{ color: config.color }}>{editingItem ? 'Edit Item' : 'New Item'}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Subcategory Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Category</label>
                <select
                  value={form.subcategory}
                  onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                  className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: '#E5E1DB', focusRing: config.color }}
                  required
                  data-testid="item-subcategory-select"
                >
                  <option value="">Select a category...</option>
                  {config.subcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.icon} {sub.name}
                    </option>
                  ))}
                  <option value="other">✏️ Other (Custom)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: '#E5E1DB' }}
                  placeholder="e.g., Dentist appointment, Water bill, etc."
                  required
                  data-testid="item-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Date (optional)</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: '#E5E1DB' }}
                  data-testid="item-date-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#2C2416' }}>Notes (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: '#E5E1DB' }}
                  placeholder="Additional details..."
                  data-testid="item-notes-input"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="px-6 py-3 rounded-sm transition-all duration-200 hover:shadow-md"
                  style={{ backgroundColor: config.color, color: 'white' }}
                  data-testid="submit-item-button"
                >
                  {editingItem ? 'Update' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingItem(null); }}
                  className="px-6 py-3 bg-white border rounded-sm transition-all duration-200"
                  style={{ borderColor: '#E5E1DB' }}
                  data-testid="cancel-item-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Subcategory Tabs */}
        <Tabs value={selectedSubcategory} onValueChange={setSelectedSubcategory} className="w-full">
          <TabsList className="mb-6 flex flex-wrap gap-2 bg-transparent border-b pb-2" style={{ borderColor: '#E5E1DB' }}>
            {config.subcategories.map((sub) => {
              const count = getItemsBySubcategory(sub.id).length;
              return (
                <TabsTrigger
                  key={sub.id}
                  value={sub.id}
                  className="px-4 py-2 rounded-sm border data-[state=active]:border-2"
                  style={{
                    borderColor: selectedSubcategory === sub.id ? config.color : '#E5E1DB',
                    backgroundColor: selectedSubcategory === sub.id ? config.color + '10' : 'white',
                    color: selectedSubcategory === sub.id ? config.color : '#8B7355'
                  }}
                  data-testid={`tab-${sub.id}`}
                >
                  <span className="mr-2">{sub.icon}</span>
                  <span>{sub.name}</span>
                  {count > 0 && (
                    <span 
                      className="ml-2 px-2 py-0.5 text-xs rounded-full"
                      style={{ backgroundColor: config.color, color: 'white' }}
                    >
                      {count}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {config.subcategories.map((sub) => {
            const subcategoryItems = getItemsBySubcategory(sub.id);
            return (
              <TabsContent key={sub.id} value={sub.id} data-testid={`content-${sub.id}`}>
                <div className="bg-white border rounded-sm shadow-sm p-8" style={{ borderColor: '#E5E1DB' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">{sub.icon}</span>
                    <h2 className="text-2xl font-light" style={{ color: config.color }}>{sub.name}</h2>
                  </div>

                  {subcategoryItems.length === 0 ? (
                    <p className="text-center py-12" style={{ color: '#8B7355' }}>
                      No items yet. Click "Add Item" to get started.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {subcategoryItems.map((item) => (
                        <div
                          key={item.id}
                          className={`p-5 border rounded-sm transition-all duration-200 hover:shadow-md ${item.status === 'done' ? 'opacity-50' : ''}`}
                          style={{ borderLeftWidth: '4px', borderLeftColor: config.color, borderColor: '#E5E1DB' }}
                          data-testid={`item-${item.id}`}
                        >
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <button
                                onClick={() => handleMarkDone(item)}
                                className="w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all hover:bg-green-100 hover:border-green-500"
                                style={{ borderColor: item.status === 'done' ? '#16A34A' : '#D1D5DB' }}
                                data-testid={`item-done-${item.id}`}
                                title="Mark as done"
                              >
                                {item.status === 'done' && <Check className="w-3 h-3" style={{ color: '#16A34A' }} />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <h3 className={`font-medium text-base mb-1 ${item.status === 'done' ? 'line-through' : ''}`} style={{ color: '#2C2416' }}>{item.title}</h3>
                                {item.notes && (
                                  <p className="text-sm mb-1" style={{ color: '#8B7355' }}>{item.notes}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {item.date && (
                                <span className="font-mono text-xs px-2 py-1 rounded-sm" style={{ backgroundColor: '#F5F0E8', color: '#2C2416' }}>
                                  {new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              )}
                              <button
                                onClick={() => handleEdit(item)}
                                className="p-1.5 rounded-sm transition-all hover:bg-gray-100"
                                data-testid={`item-edit-${item.id}`}
                                title="Edit"
                              >
                                <Edit2 className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="p-1.5 rounded-sm transition-all hover:bg-red-50"
                                data-testid={`item-delete-${item.id}`}
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </main>
    </div>
  );
};

export default PersonalCategory;